import { useEffect, useState, useContext } from 'react'
import moment from 'moment'

import LoadingContext from '../../context/Loading/Loading'

import {
  formatCurrency,
  formatDateTime,
  getDefaultSeason,
  createWorkbook,
  saveWorkbook,
  addWorksheet,
  setHeaders,
  setColumnsAndRows,
  currencyFormat,
  DEFAULTNUMHEADERS,
} from '../../utilities/helpers'

import {
  getSeasons,
  getFlexPackDeferredRevenueReport,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Alert from 'react-bootstrap/Alert'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'

import { ReportActions } from '../ReportActions'
import { SeasonSelector } from '../SeasonSelector'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import FlexPackDeferredRevenueEmailSchedule from './FlexPackDeferredRevenueEmailSchedule'

export default function FlexPackDeferredRevenueWrapper({ organization }) {
  const { showLoading, hideLoading, isLoading } = useContext(LoadingContext)

  const [seasons, setSeasons] = useState()
  const [selectedSeason, setSelectedSeason] = useState()
  const [asOfDate, setAsOfDate] = useState(() => {
    return moment().startOf('day').toDate()
  })
  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)
  const [data, setData] = useState(null)

  const loadSeasons = () => {
    getSeasons(organization.uuid)
      .then((res) => {
        setSeasons(res.data)
        const selected = getDefaultSeason(res.data)
        if (selected) setSelectedSeason(selected?.id)
        hideLoading()
      })
      .catch((err) => {
        console.error(err)
        hideLoading()
      })
  }

  const loadReport = () => {
    if (!selectedSeason || !asOfDate) return
    setIsLoadingData(true)
    getFlexPackDeferredRevenueReport(
      organization.uuid,
      selectedSeason,
      asOfDate.toISOString()
    )
      .then((res) => {
        setData(res.data)
        setIsLoadingData(false)
        completeRefresh()
      })
      .catch((err) => {
        console.error(err)
        setIsLoadingData(false)
        completeRefresh()
      })
  }

  useEffect(() => {
    showLoading()
    loadSeasons()
  }, [])

  useEffect(() => {
    if (selectedSeason && asOfDate) loadReport()
  }, [selectedSeason, asOfDate, refreshToken])

  const handleExcelExport = async () => {
    if (!data) return

    const workbook = createWorkbook()
    const asOfStr = formatDateTime(moment(asOfDate), 'dateOnly')
    const headers = [
      `As of ${asOfStr}`,
      `Period: ${formatDateTime(moment(data.periodStart), 'dateOnly')} – ${formatDateTime(moment(data.periodEnd), 'dateOnly')}`,
    ]

    const rollforwardSheet = addWorksheet('Rollforward', workbook)
    setHeaders(
      'Flex Pack Deferred Revenue Rollforward',
      organization?.name || '',
      rollforwardSheet,
      headers
    )
    const rf = data.rollforward || {}
    const rollforwardRows = [
      {
        line: 'Opening deferred revenue',
        amount: rf.openingDeferred,
      },
      {
        line: '+ Collections in period',
        amount: rf.collectionsInPeriod,
      },
      {
        line: '− Redeemed this period',
        amount: rf.redeemedInPeriod,
      },
      {
        line: '− Breakage this period',
        amount: rf.breakageInPeriod,
      },
      {
        line: '= Closing deferred revenue',
        amount: rf.closingDeferred,
      },
      {
        line: 'Journal entry (Dr Deferred / Cr Ticket Revenue)',
        amount: rf.journalEntry?.amount,
      },
    ]
    setColumnsAndRows(
      ['line', 'amount'],
      rollforwardRows,
      (row) => ({
        line: row.line,
        amount: Number(row.amount || 0),
      }),
      rollforwardSheet
    )
    currencyFormat(2, rollforwardSheet, DEFAULTNUMHEADERS + headers.length)

    const byFlexPackSheet = addWorksheet('By Flex Pack', workbook)
    setHeaders(
      'Deferred Revenue by Flex Pack',
      organization?.name || '',
      byFlexPackSheet,
      headers
    )
    setColumnsAndRows(
      [
        'flex_pack_name',
        'game_tickets',
        'orders',
        'vouchers_sold',
        'vouchers_redeemed',
        'vouchers_outstanding',
        'allocated',
        'redeemed',
        'breakage',
        'unearned',
      ],
      data.byFlexPack || [],
      (row) => ({
        flex_pack_name: row.flexPackName,
        game_tickets: row.gameTickets,
        orders: row.orderCount,
        vouchers_sold: row.vouchersSold,
        vouchers_redeemed: row.vouchersRedeemed,
        vouchers_outstanding: row.vouchersOutstanding,
        allocated: Number(row.allocated || 0),
        redeemed: Number(row.redeemed || 0),
        breakage: Number(row.breakage || 0),
        unearned: Number(row.unearned || 0),
      }),
      byFlexPackSheet
    )
    currencyFormat(7, byFlexPackSheet, DEFAULTNUMHEADERS + headers.length)
    currencyFormat(8, byFlexPackSheet, DEFAULTNUMHEADERS + headers.length)
    currencyFormat(9, byFlexPackSheet, DEFAULTNUMHEADERS + headers.length)
    currencyFormat(10, byFlexPackSheet, DEFAULTNUMHEADERS + headers.length)

    const byEventSheet = addWorksheet('By Event', workbook)
    setHeaders(
      'Deferred Revenue by Redemption Event',
      organization?.name || '',
      byEventSheet,
      headers
    )
    setColumnsAndRows(
      ['event_name', 'recognition_date', 'vouchers_redeemed', 'redeemed'],
      data.byEvent || [],
      (row) => ({
        event_name: row.eventName,
        recognition_date: row.recognitionDate
          ? formatDateTime(moment(row.recognitionDate), 'dateOnly')
          : '',
        vouchers_redeemed: row.vouchersRedeemed,
        redeemed: Number(row.redeemed || 0),
      }),
      byEventSheet
    )
    currencyFormat(4, byEventSheet, DEFAULTNUMHEADERS + headers.length)

    saveWorkbook(workbook, 'flex-pack-deferred-revenue-report')
      .then()
      .catch((err) => alert(err.message))
  }

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : (
        <section className="spacer-sm">
          <header className="section-header-sm">
            <div className="section-heading">
              <h1>Flex Pack Deferred Revenue</h1>
            </div>
            <p className="section-header-desc">
              Month-end unearned flex pack liability and voucher recognition
              rollforward. Flex pack purchase net sits as deferred revenue,
              allocated equally per voucher. Redeemed vouchers recognize on
              event start (or claim date fallback); unredeemed vouchers
              recognize as breakage at season end. Pick a season and an as-of
              date (defaults to today) to close the period.
            </p>
          </header>

          {seasons?.length > 0 ? (
            <>
              <ReportActions
                isLoading={isLoadingData}
                onRefresh={refresh}
                isRefreshing={isLoadingData}
                selected={selectedSeason}
                startDate={asOfDate}
                setStartDate={setAsOfDate}
                showTime={false}
                data={data}
                downloadReport={handleExcelExport}
              >
                <SeasonSelector
                  seasons={seasons}
                  selected={selectedSeason}
                  setSelected={setSelectedSeason}
                  isLoading={isLoadingData}
                />
              </ReportActions>

              <FlexPackDeferredRevenueEmailSchedule
                organizationUUID={organization?.uuid}
                selectedSeason={selectedSeason}
                asOfDate={asOfDate}
              />

              <Results
                isLoading={isLoadingData}
                data={data}
                asOfDate={asOfDate}
              />
            </>
          ) : (
            <Card body>
              <EmptyContainer style="center lg">
                <p>No seasons created</p>
                <p className="subtitle">
                  Create a season with flex packs to track deferred revenue.
                </p>
              </EmptyContainer>
            </Card>
          )}
        </section>
      )}
    </>
  )
}

function pickDataQualityCount(dataQuality, keys) {
  if (!dataQuality || typeof dataQuality !== 'object') return 0
  for (const key of keys) {
    const value = dataQuality[key]
    if (value != null && Number(value) > 0) return Number(value)
  }
  return 0
}

function getDataQualityIssues(dataQuality) {
  if (!dataQuality || typeof dataQuality !== 'object') return []

  const issues = []

  const missingRecognition = pickDataQualityCount(dataQuality, [
    'claimedWithoutRecognitionDate',
    'claimedMissingRecognitionDate',
    'claimedVouchersMissingRecognitionDate',
    'missingRecognitionDateCount',
    'unrecognizedClaimedCount',
    'claimedMissingRecognitionDateCount',
  ])
  if (missingRecognition > 0) {
    issues.push(
      `${missingRecognition} claimed voucher${missingRecognition === 1 ? '' : 's'} missing a usable recognition date`
    )
  }

  const missingVouchers = pickDataQualityCount(dataQuality, [
    'missingVoucherRows',
    'legacyMissingVoucherRecords',
    'missingVoucherRecords',
    'legacyMissingVouchers',
    'missingVoucherCount',
    'anonymousVoucherSlots',
    'legacyMissingVoucherRecordCount',
    'purchasesWithoutVoucherSlots',
  ])
  if (missingVouchers > 0) {
    issues.push(
      `${missingVouchers} legacy missing voucher record${missingVouchers === 1 ? '' : 's'} (anonymous unredeemed slots used)`
    )
  }

  return issues
}

function Results({ isLoading, data, asOfDate }) {
  if (!asOfDate) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>Select an as-of date</p>
          <p className="subtitle">
            Choose the month-end date for your Balance Sheet close.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card body>
        <LoadingContainer />
      </Card>
    )
  }

  if (!data) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No flex pack deferred revenue data</p>
        </EmptyContainer>
      </Card>
    )
  }

  const rf = data.rollforward || {}
  const summary = data.summary || {}
  const dataQualityIssues = getDataQualityIssues(data.dataQuality)

  return (
    <>
      {dataQualityIssues.length > 0 ? (
        <Alert variant="warning" className="mb-4">
          <strong>Data quality:</strong> {dataQualityIssues.join('; ')}.
        </Alert>
      ) : null}

      <Card body className="card-with-border mb-4">
        <h2 className="card-title-sm mb-2">How to read this report</h2>
        <p className="text-muted small mb-0">
          Cash collected for flex packs (purchase net) is held as deferred
          revenue and split equally across each voucher in the pack. When a
          voucher is redeemed, its share is recognized on the linked event start
          date, or the claim date if no event start is available. Vouchers still
          unredeemed at season end are recognized as breakage. Use the as-of
          date as your month-end Balance Sheet cut-off; the period defaults to
          the start of that month through the as-of date.
        </p>
      </Card>

      <div className="row g-3 mb-4">
        <SummaryCard
          label="Closing deferred revenue"
          value={summary.deferredRevenue}
          hint="Liability still unearned as of this date"
        />
        <SummaryCard
          label="Redeemed this period"
          value={summary.redeemedThisPeriod}
          hint="Revenue recognized from voucher redemptions"
        />
        <SummaryCard
          label="Breakage this period"
          value={summary.breakageThisPeriod}
          hint="Unredeemed vouchers recognized at season end"
        />
        <SummaryCard
          label="Outstanding vouchers"
          value={summary.vouchersOutstanding}
          hint={
            summary.vouchersSold != null
              ? `${summary.vouchersRedeemed || 0} redeemed of ${summary.vouchersSold} sold`
              : 'Unredeemed vouchers still deferred'
          }
          isCurrency={false}
        />
      </div>

      <Card body className="card-with-border mb-4">
        <h2 className="card-title-sm mb-2">Month-end rollforward</h2>
        <p className="text-muted small mb-3">
          Opening deferred + collections − redeemed − breakage = closing
          deferred. Closing should match your Balance Sheet flex pack deferred
          revenue liability. Recognized (redeemed + breakage) is the suggested
          Dr Deferred Revenue / Cr Ticket Revenue amount for the period.
        </p>
        <p className="text-muted small mb-3">
          Period {formatDateTime(moment(data.periodStart), 'dateOnly')} –{' '}
          {formatDateTime(moment(data.periodEnd), 'dateOnly')}
          {data.methodology?.allocableBase
            ? ` · Base: ${data.methodology.allocableBase}`
            : data.methodology?.description
              ? ` · ${data.methodology.description}`
              : ''}
        </p>
        <Table responsive className="mb-0">
          <tbody>
            <RollforwardRow
              label="Opening deferred revenue"
              amount={rf.openingDeferred}
            />
            <RollforwardRow
              label="+ Collections in period"
              amount={rf.collectionsInPeriod}
            />
            <RollforwardRow
              label="− Redeemed this period"
              amount={rf.redeemedInPeriod}
            />
            <RollforwardRow
              label="− Breakage this period"
              amount={rf.breakageInPeriod}
            />
            <RollforwardRow
              label="= Closing deferred revenue"
              amount={rf.closingDeferred}
              strong
            />
          </tbody>
        </Table>
        {rf.journalEntry?.amount > 0 && (
          <p className="mt-3 mb-0 small text-muted">
            Suggested journal entry: Dr{' '}
            <strong>
              {rf.journalEntry.debitAccount || 'Deferred Revenue'}
            </strong>{' '}
            {formatCurrency(rf.journalEntry.amount)} / Cr{' '}
            <strong>{rf.journalEntry.creditAccount || 'Ticket Revenue'}</strong>{' '}
            {formatCurrency(rf.journalEntry.amount)}
            {Number(rf.redeemedInPeriod || 0) > 0 ||
            Number(rf.breakageInPeriod || 0) > 0
              ? ` (${formatCurrency(rf.redeemedInPeriod || 0)} redemption + ${formatCurrency(rf.breakageInPeriod || 0)} breakage)`
              : ''}
          </p>
        )}
      </Card>

      <Card body className="card-with-border mb-4">
        <h2 className="card-title-sm mb-2">By flex pack</h2>
        <p className="text-muted small mb-3">
          One row per flex pack in the season. Allocated is total purchase-net
          consideration collected. Redeemed and Breakage are recognized amounts;
          Unearned is still sitting as deferred revenue. Redeemed + Breakage +
          Unearned should equal Allocated.
        </p>
        {(data.byFlexPack || []).length === 0 ? (
          <EmptyContainer style="center">
            <p>No flex pack allocations for this period</p>
          </EmptyContainer>
        ) : (
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th>Flex pack</th>
                <th className="text-end">Game tickets</th>
                <th className="text-end">Orders</th>
                <th className="text-end">Sold</th>
                <th className="text-end">Redeemed</th>
                <th className="text-end">Outstanding</th>
                <th className="text-end">Allocated</th>
                <th className="text-end">Redeemed $</th>
                <th className="text-end">Breakage</th>
                <th className="text-end">Unearned</th>
              </tr>
            </thead>
            <tbody>
              {data.byFlexPack.map((row) => (
                <tr key={row.flexPackId || row.flexPackName}>
                  <td>{row.flexPackName}</td>
                  <td className="text-end">{row.gameTickets}</td>
                  <td className="text-end">{row.orderCount}</td>
                  <td className="text-end">{row.vouchersSold}</td>
                  <td className="text-end">{row.vouchersRedeemed}</td>
                  <td className="text-end">{row.vouchersOutstanding}</td>
                  <td className="text-end">{formatCurrency(row.allocated)}</td>
                  <td className="text-end">{formatCurrency(row.redeemed)}</td>
                  <td className="text-end">{formatCurrency(row.breakage)}</td>
                  <td className="text-end">{formatCurrency(row.unearned)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card body className="card-with-border">
        <h2 className="card-title-sm mb-2">By redemption event</h2>
        <p className="text-muted small mb-3">
          Revenue recognized from vouchers redeemed against each event.
          Recognition date is the event start when available.
        </p>
        {(data.byEvent || []).length === 0 ? (
          <EmptyContainer style="center">
            <p>No redemption event allocations</p>
          </EmptyContainer>
        ) : (
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th>Event</th>
                <th>Recognition date</th>
                <th className="text-end">Vouchers redeemed</th>
                <th className="text-end">Redeemed</th>
              </tr>
            </thead>
            <tbody>
              {data.byEvent.map((row) => (
                <tr key={row.eventId || row.eventUuid || row.eventName}>
                  <td>{row.eventName}</td>
                  <td>
                    {row.recognitionDate
                      ? formatDateTime(moment(row.recognitionDate), 'dateOnly')
                      : '—'}
                  </td>
                  <td className="text-end">{row.vouchersRedeemed}</td>
                  <td className="text-end">{formatCurrency(row.redeemed)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  )
}

function SummaryCard({ label, value, hint, isCurrency = true }) {
  return (
    <div className="col-6 col-lg-3">
      <Card body className="card-with-border h-100">
        <p className="small text-muted mb-1">{label}</p>
        <p className="h4 mb-1">
          {isCurrency
            ? formatCurrency(value || 0)
            : value != null
              ? value
              : '—'}
        </p>
        {hint ? <p className="small text-muted mb-0">{hint}</p> : null}
      </Card>
    </div>
  )
}

function RollforwardRow({ label, amount, strong }) {
  return (
    <tr>
      <td className={strong ? 'fw-semibold' : ''}>{label}</td>
      <td className={`text-end ${strong ? 'fw-semibold' : ''}`}>
        {formatCurrency(amount || 0)}
      </td>
    </tr>
  )
}
